import { SelectOption, useFormNotification } from "@commonlibs";
import { useControlled } from "@commonlibs";
import { useState, useMemo, useEffect } from "react";
import useTranslate from "ui/locale/hooks/useTranslate";
import { createEmptyOptions, defaultFilterOption } from "./useAutocomplete.utils";
import { UseAutocompleteParams } from "./useAutocomplete.types";

/**
 * Core logic hook for the `Autocomplete` component.
 *
 * Manages three interconnected pieces of state:
 * - **search** – the text currently typed in the input (controlled or uncontrolled).
 * - **selected** – the value of the chosen option (controlled or uncontrolled).
 * - **open** – visibility of the dropdown panel.
 *
 * It also derives the `candidates` list by filtering `options` against `search`
 * using the supplied (or default) `filterOption` predicate, and notifies the
 * enclosing form via `useFormNotification` so the field participates in native
 * form validation.
 *
 * @template T - The type of each option's `value` field.
 * @param params - See {@link UseAutocompleteParams}.
 * @returns An object with:
 *   - `inputProps` – props to spread onto the search `<input>` element (`value`, `onChange`).
 *   - `selectProps` – props to spread onto the underlying `<Select>` root (`open`, `onClose`, `onOpen`, `onChange`, `value`, `options`).
 */
export default function useAutocomplete<T>({
    defaultSearch = "",
    search: searchProp,
    onSearchChange,
    defaultValue,
    value,
    onChange,
    options,
    isLoading,
    filterOption = defaultFilterOption,
    name,
    required,
    loadingText,
    nothingToShow,
}: UseAutocompleteParams<T>) {
    const translate = useTranslate();
    const [search, setSearch] = useControlled(defaultSearch, searchProp, onSearchChange);
    const [selectedOption, setSelectedOption] = useState<SelectOption<T> | undefined>(undefined); // skipcq: JS-W1042
    const [hasSettedDefault, setHasSettedDefault] = useState(false);
    const [selected, setSelected] = useControlled(defaultValue as T, value, onChange);
    const [open, setOpened] = useState(false);

    /**
     * HANDLERS
     */

    /** Opens the dropdown (if not already open) and updates the search string. */
    function handleSearch(value: string) {
        if (!open) setOpened(true);
        setSearch(value);
    }

    /**
     * Selects an option by value.
     * Clears the search when the empty sentinel (`""`) is selected and
     * syncs the input text with the chosen option's label via a deferred
     * `setTimeout` so the state update lands after the current render cycle.
     * No-ops while `isLoading` is `true`.
     */
    function handleSelect(value: T) {
        if (isLoading) return;
        else if (value === "") setSearch("");
        setSelected(value);
        setTimeout(() => {
            const option = options?.find((o) => o.value === value);
            setSelectedOption(option);
            setSearch(String(option?.label || ""));
        });
    }

    /**
     * Closes the dropdown and, if the search text matches an option label
     * exactly, commits that option as the selected value.
     * The label-match lookup is deferred so it runs after the closing
     * animation and any pending state updates settle.
     */
    function handleClose() {
        setOpened(false);
        setTimeout(() => {
            const searchOption = options?.find((o) => o.label === search);
            if (searchOption) {
                setSelected(searchOption.value);
                setSelectedOption(searchOption);
            }
        });
    }

    /**
     * EFFECTS
     */

    /**
     * Seed the search input with the label of `defaultValue` once the options
     * array becomes available. Runs only once per mount (guarded by
     * `hasSettedDefault`) and only when the input is still empty, so it does
     * not overwrite text the user has already typed.
     */
    useEffect(() => {
        if (hasSettedDefault || search !== "") return;
        const option = options?.find((o) => o.value === defaultValue);
        if (option) {
            setSearch(String(option?.label || ""));
            setHasSettedDefault(true);
        }
    }, [defaultValue, options]);

    const candidates = useMemo(() => {
        if (isLoading) return createEmptyOptions(loadingText || translate("loading"));
        if (search === "") return options;
        const filteredCandidates = options?.filter((o) => filterOption(o, search)) || [];
        if (filteredCandidates.length === 0) {
            return createEmptyOptions(nothingToShow || translate("noResultsForThisSearch", { ns: "error" }));
        }
        return filteredCandidates;
    }, [search, options, isLoading]) as SelectOption<T>[];

    const isValid = !isLoading && (selected ? search !== "" && selectedOption?.label === search : !required);

    useFormNotification(name, selected, isValid);

    return {
        inputProps: { value: search, onChange: handleSearch },
        selectProps: {
            open,
            onClose: handleClose,
            onOpen: () => setOpened(true),
            onChange: handleSelect,
            value: selected,
            options: candidates,
        },
    };
}
